"use client";

import { useEffect, useMemo } from "react";

import {
  getRequiredFieldsForSlug,
} from "../validation/getRequiredFieldsBySlug";
import { SignupFlowSlug } from "../constants";

import { useSignupFlowStore } from "./useSignUpFlowStore";
import { useSignupFlowErrorStore } from "./useSignUpFlowErrorStore";

export const usePageStatus = (slug: SignupFlowSlug) => {
  const { validatedFields } = useSignupFlowErrorStore((state) => state);
  const { activeCatId, cats } = useSignupFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
  }));

  const requiredFields = useMemo(() => getRequiredFieldsForSlug(slug), [ slug, cats ]);
  const validated = new Set(validatedFields[slug] || []);

  useEffect(() => {
    const filtered = (validatedFields[slug] || []).filter((field) => requiredFields.includes(field));

    if (filtered.length !== (validatedFields[slug]?.length || 0)) {
      useSignupFlowErrorStore.setState((state) => ({
        validatedFields: {
          ...state.validatedFields,
          [slug]: filtered,
        },
      }));
    }
  }, [ slug, validatedFields, requiredFields ]);

  const isCatComplete = (catId: string): boolean => requiredFields
    .filter((key) => key.startsWith(`cat[${catId}]`))
    .every((fieldKey) => validated.has(fieldKey));

  const catCompletionMap: Record<string, boolean> = {};
  Object.keys(cats).forEach((catId) => {
    if (!cats[catId].softDelete) {
      catCompletionMap[catId] = isCatComplete(catId);
    }
  });

  const isPageComplete = requiredFields.every((fieldKey) => validated.has(fieldKey));

  return {
    isPageComplete,
    isCatComplete: activeCatId ? isCatComplete(activeCatId) : false,
    catCompletionMap,
  };
};
